/**
 * Admin API — user management endpoints. dwa_admin role only.
 * Mirrors /admin/users/* routes in app/services/agent/app/main.py.
 */
import { authService, authFetch } from './auth';
import { API_BASE } from './api';
import type { User, UserRole, ClientAccessEntry, UserPermission } from '../types';

function authHeaders(): HeadersInit {
  const t = authService.getAccessToken();
  return t
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` }
    : { 'Content-Type': 'application/json' };
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const resp = await authFetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const errBody = await resp.json();
      detail = errBody.detail || errBody.error || detail;
    } catch { /* swallow */ }
    throw new Error(detail);
  }
  if (resp.status === 204) return undefined as unknown as T;
  return resp.json() as Promise<T>;
}

export interface AdminCreateUserRequest {
  email: string;
  password: string;
  display_name?: string;
  role: UserRole;
  client_access?: ClientAccessEntry[] | null;
  permissions?: UserPermission[];
}

export interface AdminUpdateUserRequest {
  display_name?: string;
  is_active?: boolean;
  role?: UserRole;
  client_access?: ClientAccessEntry[] | null;
  password?: string;
  /** Sent only when changed (a super admin is needed to change them). */
  permissions?: UserPermission[];
}

export async function listUsers(includeInactive = false): Promise<User[]> {
  const qs = includeInactive ? '?includeInactive=true' : '';
  return request<User[]>('GET', `/admin/users${qs}`);
}

export async function createUser(body: AdminCreateUserRequest): Promise<User> {
  return request<User>('POST', '/admin/users', body);
}

export async function updateUser(userId: string, body: AdminUpdateUserRequest): Promise<User> {
  return request<User>('PATCH', `/admin/users/${userId}`, body);
}

export async function deactivateUser(userId: string): Promise<void> {
  return request<void>('DELETE', `/admin/users/${userId}`);
}

// ─── PDF Email Recipients ─────────────────────────────────────────────────

export type ReportType = 'monthly' | 'weekly_scorecard' | 'weekly_plc_report' | 'backup_alert';

export interface EmailRecipient {
  id: number;
  client_id: string;
  email: string;
  display_name: string | null;
  report_type: ReportType;
  created_at: string;
}

export interface CreateEmailRecipientRequest {
  client_id: string;
  email: string;
  display_name?: string;
  report_type: ReportType;
}

export async function listEmailRecipients(clientId?: string): Promise<EmailRecipient[]> {
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<EmailRecipient[]>('GET', `/admin/email-recipients${qs}`);
}

export async function createEmailRecipient(body: CreateEmailRecipientRequest): Promise<EmailRecipient> {
  return request<EmailRecipient>('POST', '/admin/email-recipients', body);
}

export async function deleteEmailRecipient(id: number): Promise<void> {
  return request<void>('DELETE', `/admin/email-recipients/${id}`);
}
